import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { motion } from "framer-motion";
import { Copy, Check, FloppyDisk, Spinner, Trash } from "@phosphor-icons/react";
import ReactMarkdown from "react-markdown";

export function BulkOutputSlot({
  output, idx, isCurrentlyGenerating, result,
  contentTypes, platforms,
  onUpdateOutput, onRemoveOutput, onCopy, onSave,
  copiedId, savedId, outputCount,
}) {
  return (
    <motion.div
      key={output.id}
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className={`bg-[var(--bg-card)] border rounded-lg p-4 transition-colors ${
        isCurrentlyGenerating
          ? "border-[#FF4400]/50 shadow-[0_0_15px_rgba(255,68,0,0.1)]"
          : result?.success
          ? "border-[var(--success)]/20"
          : result && !result.success
          ? "border-red-500/20"
          : "border-[var(--border)]"
      }`}
      data-testid={`output-slot-${idx}`}
    >
      <div className="flex items-center gap-3">
        <span className="font-['JetBrains_Mono'] text-xs text-[var(--text-muted)] shrink-0 w-6">{String(idx + 1).padStart(2, "0")}</span>

        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
          <Select value={output.content_type || "placeholder"} onValueChange={(v) => onUpdateOutput(output.id, "content_type", v === "placeholder" ? "" : v)}>
            <SelectTrigger className="bg-[var(--bg-base)] border-[var(--border-strong)] text-[var(--text-primary)] h-9 text-xs" data-testid={`output-type-${idx}`}>
              <SelectValue placeholder="Content Type" />
            </SelectTrigger>
            <SelectContent className="bg-[var(--bg-raised)] border-[var(--border-strong)]">
              <SelectItem value="placeholder" className="text-[var(--text-muted)]">Select type...</SelectItem>
              {contentTypes.map((t) => (
                <SelectItem key={t} value={t} className="text-[var(--text-secondary)] focus:bg-[var(--accent-subtle)] focus:text-white text-xs">{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={output.platform || "placeholder"} onValueChange={(v) => onUpdateOutput(output.id, "platform", v === "placeholder" ? "" : v)}>
            <SelectTrigger className="bg-[var(--bg-base)] border-[var(--border-strong)] text-[var(--text-primary)] h-9 text-xs" data-testid={`output-platform-${idx}`}>
              <SelectValue placeholder="Platform" />
            </SelectTrigger>
            <SelectContent className="bg-[var(--bg-raised)] border-[var(--border-strong)]">
              <SelectItem value="placeholder" className="text-[var(--text-muted)]">Select platform...</SelectItem>
              {platforms.map((p) => (
                <SelectItem key={p} value={p} className="text-[var(--text-secondary)] focus:bg-[var(--accent-subtle)] focus:text-white text-xs">{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-1 shrink-0">
          {isCurrentlyGenerating && <Spinner size={16} className="animate-spin text-[#FF4400]" />}
          {result?.success && (
            <>
              <button onClick={() => onCopy(output)} className="p-1.5 rounded-md text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] transition-colors" data-testid={`copy-result-${idx}`} title="Copy draft">
                {copiedId ? <Check size={14} className="text-[var(--success)]" /> : <Copy size={14} />}
              </button>
              <button onClick={() => onSave(output)} disabled={savedId} className="p-1.5 rounded-md text-[var(--text-muted)] hover:text-[var(--success)] hover:bg-[var(--success)]/10 transition-colors disabled:opacity-30" data-testid={`save-result-${idx}`} title={savedId ? "Saved" : "Save to history"}>
                {savedId ? <Check size={14} className="text-[var(--success)]" /> : <FloppyDisk size={14} />}
              </button>
            </>
          )}
          {result && !result.success && <span className="text-[10px] text-red-400 font-['JetBrains_Mono']">FAILED</span>}
          <button onClick={() => onRemoveOutput(output.id)} disabled={outputCount <= 1} className="p-1.5 rounded-md text-[var(--text-muted)] hover:text-red-400 hover:bg-red-400/10 transition-colors disabled:opacity-20" data-testid={`remove-output-${idx}`}>
            <Trash size={14} />
          </button>
        </div>
      </div>

      {result?.success && (
        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} className="mt-3 pt-3 border-t border-[var(--border)]">
          <p className="text-xs font-['JetBrains_Mono'] text-[#FF4400] mb-1">{output.content_type} — {output.platform}</p>
          <div className="text-xs text-[var(--text-secondary)] leading-relaxed line-clamp-4 prose prose-xs max-w-none prose-headings:text-[var(--text-primary)] prose-strong:text-[var(--text-primary)]" data-testid={`result-preview-${idx}`}>
            <ReactMarkdown>{result.data.draft?.substring(0, 300) + "..."}</ReactMarkdown>
          </div>
        </motion.div>
      )}

      {result && !result.success && <div className="mt-2 text-xs text-red-400">{result.error}</div>}
    </motion.div>
  );
}
